
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { sendEmail, fileToAttachment, type EmailAttachment } from '@/services/emailService';
import { getRegistrationEmailTemplate, getPasswordResetEmailTemplate, getContactConfirmationEmailTemplate, getNewsletterTemplate } from '@/utils/emailTemplates';
import { toast } from 'sonner';

const EmailServiceTest = () => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('Testmail von Hoss Immo');
  const [body, setBody] = useState('');
  const [isTemplate, setIsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
      
      try {
        const newAttachments = await Promise.all(
          files.map(file => fileToAttachment(file))
        );
        setAttachments(prev => [...prev, ...newAttachments]);
      } catch (error) {
        console.error('Fehler beim Konvertieren der Dateien:', error);
        toast.error('Fehler beim Hinzufügen der Anhänge');
      }
    }
  };

  const removeAttachment = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleTemplateModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsTemplate(e.target.checked);
    if (e.target.checked) {
      setBody('');
    }
  };

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTemplateName(e.target.value);
    
    switch(e.target.value) {
      case 'registration':
        setBody(getRegistrationEmailTemplate('Testnutzer'));
        setSubject('Willkommen bei Hoss Immo - Registrierung erfolgreich');
        break;
      case 'password-reset':
        setBody(getPasswordResetEmailTemplate('Testnutzer', 'https://app.hoss-immo.de/reset-password?token=test-token'));
        setSubject('Hoss Immo - Ihr Link zum Zurücksetzen des Passworts');
        break;
      case 'contact':
        setBody(getContactConfirmationEmailTemplate('Testnutzer', 'Dies ist eine Testnachricht für das Kontaktformular. Bitte ignorieren Sie diese Nachricht.'));
        setSubject('Hoss Immo - Ihre Kontaktanfrage ist eingegangen');
        break;
      case 'newsletter':
        setBody(getNewsletterTemplate('Testnutzer', '<p>Hier ist der Newsletter-Inhalt mit <strong>formatiertem Text</strong>.</p><p>Mehrere Absätze sind möglich.</p>'));
        setSubject('Hoss Immo Newsletter - Neuigkeiten im Mai');
        break;
      default:
        // Nichts tun, wenn keine Vorlage ausgewählt ist
        break;
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!to || (!body && !isTemplate)) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    setIsSending(true);
    
    try {
      let emailBody = body;
      
      if (isTemplate) {
        switch(templateName) {
          case 'registration':
            emailBody = getRegistrationEmailTemplate('Testnutzer');
            break;
          case 'password-reset':
            emailBody = getPasswordResetEmailTemplate('Testnutzer', 'https://app.hoss-immo.de/reset-password?token=test-token');
            break;
          case 'contact':
            emailBody = getContactConfirmationEmailTemplate('Testnutzer', 'Dies ist eine Testnachricht für das Kontaktformular.');
            break;
          case 'newsletter':
            emailBody = getNewsletterTemplate('Testnutzer', '<p>Hier ist der Newsletter-Inhalt.</p>');
            break;
          default:
            // Verwende den benutzerdefinierten Inhalt, wenn keine Vorlage ausgewählt ist
            break;
        }
      }
      
      const result = await sendEmail({
        to,
        subject,
        body: emailBody,
        attachments: attachments.length > 0 ? attachments : undefined
      });
      
      if (result.success) {
        toast.success(result.message);
        // Formular zurücksetzen bei Erfolg
        setBody('');
        setSelectedFiles([]);
        setAttachments([]);
      } else {
        toast.error(`Fehler beim Senden: ${result.message}`);
      }
    } catch (error) {
      console.error('Fehler beim Senden der E-Mail:', error);
      toast.error('Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-3xl">
      <div className="bg-black/20 rounded-xl p-6">
        <h1 className="text-2xl font-bold mb-6">E-Mail-Service</h1>
        
        <form onSubmit={handleSendEmail} className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-1">
              Empfänger <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="email@beispiel.de"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">
              Betreff <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Betreff der E-Mail"
              required
            />
          </div>
          
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="checkbox"
              id="templateMode"
              checked={isTemplate}
              onChange={handleTemplateModeChange}
              className="rounded text-[#41c7f0]"
            />
            <label htmlFor="templateMode">Vorgefertigte Vorlage verwenden</label>
          </div>
          
          {isTemplate ? (
            <div>
              <label className="block text-sm font-medium mb-1">
                Vorlage auswählen
              </label>
              <select
                value={templateName}
                onChange={handleTemplateChange}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">-- Vorlage wählen --</option>
                <option value="registration">Registrierungsbestätigung</option>
                <option value="password-reset">Passwort zurücksetzen</option>
                <option value="contact">Kontaktanfrage-Bestätigung</option>
                <option value="newsletter">Newsletter</option>
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">
                Nachrichtentext (HTML) <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="<p>Hier kann HTML-Inhalt eingegeben werden.</p>"
                className="min-h-32 font-mono"
                required={!isTemplate}
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-1">
              Anhänge (optional)
            </label>
            <div className="flex items-center gap-2 mb-2">
              <Input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                multiple
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('file-upload')?.click()}
                className="w-full"
              >
                Dateien auswählen
              </Button>
            </div>
            
            {selectedFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-sm font-medium">Ausgewählte Dateien:</p>
                <ul className="bg-black/20 rounded-md p-2">
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="flex justify-between items-center text-sm py-1">
                      <span className="truncate max-w-xs">{file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-red-500 hover:text-red-700"
                        onClick={() => removeAttachment(index)}
                      >
                        ✕
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <Button
            type="submit"
            className="w-full bg-[#41c7f0] hover:bg-[#3ab1d9]"
            disabled={isSending}
          >
            {isSending ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                E-Mail wird gesendet...
              </span>
            ) : (
              'E-Mail senden'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default EmailServiceTest;
